import { useEffect, useRef, useState } from "react";
import { getJupidEmbedToken } from "../../api/taxes/taxesServices";

const JUPID_PARTNER_ID = "halper";
const JUPID_APP_URL = "https://app.jupid.com";
const TOKEN_REFRESH_MS = 4 * 60 * 1000;

function loadJupidScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector("script[data-jupid-sdk]")) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.jupidSdk = "true";
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load Jupid SDK"));
    document.body.appendChild(script);
  });
}

export default function Taxes() {
  const containerRef = useRef(null);
  const tokenRef = useRef(null);
  const cancelledRef = useRef(false);
  const intervalRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    cancelledRef.current = false;

    const refreshToken = async () => {
      const token = await getJupidEmbedToken();
      if (cancelledRef.current) return;
      tokenRef.current = token;

      if (typeof window.JupidEmbed?.updateToken === "function") {
        window.JupidEmbed.updateToken(token);
      } else if (typeof window.JupidEmbed?.update === "function") {
        window.JupidEmbed.update({ token });
      } else {
        try { window.JupidEmbed?.unmount?.(); } catch {}
        window.JupidEmbed.mount({
          partnerId: JUPID_PARTNER_ID,
          token,
          container: containerRef.current,
          shell: "topbar",
          chat: "disabled",
          appUrl: JUPID_APP_URL,
        });
      }
    };

    const init = async () => {
      try {
        const token = await getJupidEmbedToken();
        if (cancelledRef.current) return;
        tokenRef.current = token;

        await loadJupidScript(`${JUPID_APP_URL}/embed.js`);
        if (cancelledRef.current) return;

        window.JupidEmbed.mount({
          partnerId: JUPID_PARTNER_ID,
          token: tokenRef.current,
          container: containerRef.current,
          initialPath: "/",
          shell: "topbar",
          chat: "disabled",
          appUrl: JUPID_APP_URL,
        });

        if (!cancelledRef.current) setStatus("ready");

        intervalRef.current = setInterval(async () => {
          if (cancelledRef.current) return;
          try {
            await refreshToken();
          } catch (err) {
            console.error("Jupid token refresh failed:", err);
          }
        }, TOKEN_REFRESH_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        console.error("Jupid embed error:", err);
        setErrorMessage(err?.response?.data?.detail || err.message || "Failed to load Taxes");
        setStatus("error");
      }
    };

    init();

    return () => {
      cancelledRef.current = true;
      clearInterval(intervalRef.current);
      try { window.JupidEmbed?.unmount?.(); } catch {}
    };
  }, []);

  return (
    <div className="flex flex-col h-full w-full">
      {status === "loading" && (
        <div className="flex items-center justify-center flex-1">
          <span className="text-neutral-400 text-sm">Loading Taxes...</span>
        </div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center justify-center flex-1 gap-2">
          <span className="text-red-500 text-sm">{errorMessage}</span>
        </div>
      )}

      <div
        ref={containerRef}
        id="jupid-embed"
        style={{
          height: "100%",
          minHeight: 720,
          display: status === "ready" ? "block" : "none",
        }}
      />
    </div>
  );
}
